import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { events as evtApi } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Clock, Calendar, MapPin, Loader2, CheckCircle2, XCircle, Ticket, ArrowLeft } from 'lucide-react';

// Countdown formatter — returns { hh, mm, ss, expired }
function useCountdown(targetIso) {
  const target = useMemo(() => (targetIso ? new Date(targetIso).getTime() : null), [targetIso]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!target) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [target]);
  if (!target) return { hh: '--', mm: '--', ss: '--', expired: false, msLeft: null };
  const ms = Math.max(0, target - now);
  const totalSec = Math.floor(ms / 1000);
  const hh = String(Math.floor(totalSec / 3600)).padStart(2, '0');
  const mm = String(Math.floor((totalSec % 3600) / 60)).padStart(2, '0');
  const ss = String(totalSec % 60).padStart(2, '0');
  return { hh, mm, ss, expired: ms === 0, msLeft: ms };
}

// Attendee lands here from the "A ticket opened up" email. If their waitlist
// offer window is still open, they see a 48h countdown and a Buy button.
// When the timer runs out, the page automatically flips to the public-discount
// view (and the backend has already flipped the listing status).
export default function ResaleClaim() {
  const { eid, listingId } = useParams();
  const [listing, setListing] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await evtApi.resaleListing(eid, listingId);
      setListing(r);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Listing not found');
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eid, listingId]);

  const timer = useCountdown(listing?.reservedUntil);

  // When the countdown hits zero, refetch so the UI mirrors the backend flip.
  useEffect(() => {
    if (timer.expired && listing?.status === 'reserved_for_waitlist') load();
    // eslint-disable-next-line
  }, [timer.expired]);

  const buy = async () => {
    setBusy(true);
    try {
      const s = await evtApi.buyResale(eid, listingId, window.location.origin);
      if (!s?.url) throw new Error('Stripe returned no URL');
      window.location.href = s.url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 410) {
        toast({ title: 'Offer expired', description: 'The listing is now public at a discount.' });
        await load();
      } else {
        toast({ title: 'Checkout failed', description: detail || e?.message || 'Try again' });
      }
      setBusy(false);
    }
  };

  if (err) return (
    <div className="max-w-lg mx-auto pbb-card p-10 text-center" data-testid="resale-claim-error">
      <div className="font-semibold text-white">{err}</div>
      <Link to="/resale" className="text-green-400 text-sm mt-3 inline-flex items-center gap-1"><ArrowLeft size={12}/> Resale market</Link>
    </div>
  );
  if (!listing) return (
    <div className="flex items-center gap-2 text-gray-400" data-testid="resale-claim-loading">
      <Loader2 className="animate-spin" size={16}/> Loading offer…
    </div>
  );

  const ev = listing.event || {};
  const isSold = listing.status === 'sold';
  const isWithdrawn = listing.status === 'withdrawn';
  const isReserved = listing.status === 'reserved_for_waitlist';
  const isPublic = listing.status === 'active_public';
  const isMine = listing.isSeller;
  const showTimer = isReserved && listing.isWaitlistClaimant && !timer.expired;
  const original = Number(listing.priceUsd || 0);
  const publicPrice = Number(listing.publicPriceUsd || 0);

  return (
    <div className="max-w-lg mx-auto space-y-4" data-testid="resale-claim-page">
      <Link to="/resale" className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white">
        <ArrowLeft size={14}/> Resale market
      </Link>

      <div className="pbb-card p-5 space-y-4">
        <div>
          <div className="text-[10px] tracking-widest text-green-400 uppercase">Resale ticket</div>
          <div className="text-lg font-bold text-white">{ev.title || 'Event'}</div>
          <div className="text-xs text-gray-400 mt-1 flex flex-wrap gap-3">
            {ev.date && <span className="inline-flex items-center gap-1"><Calendar size={11}/> {ev.date}{ev.time ? ` · ${ev.time}` : ''}</span>}
            {(ev.location || ev.city) && <span className="inline-flex items-center gap-1"><MapPin size={11}/> {[ev.location, ev.city].filter(Boolean).join(', ')}</span>}
          </div>
        </div>

        {isMine && (
          <div className="rounded-lg bg-blue-500/10 border border-blue-500/40 p-3 text-xs text-blue-200" data-testid="resale-claim-is-seller">
            This is your own listing. Head to <Link to="/my/tickets" className="underline">My Tickets</Link> to withdraw it.
          </div>
        )}

        {isSold && (
          <div className="rounded-lg bg-gray-500/10 border border-gray-500/40 p-4 text-center text-sm text-gray-300 inline-flex flex-col items-center gap-1 w-full" data-testid="resale-claim-sold">
            <XCircle size={22} className="text-gray-400"/> This ticket has already been sold.
          </div>
        )}
        {isWithdrawn && (
          <div className="rounded-lg bg-gray-500/10 border border-gray-500/40 p-4 text-center text-sm text-gray-300 inline-flex flex-col items-center gap-1 w-full" data-testid="resale-claim-withdrawn">
            <XCircle size={22} className="text-gray-400"/> The seller pulled this ticket off the market.
          </div>
        )}

        {showTimer && (
          <div className="rounded-xl bg-gradient-to-b from-green-500/15 to-black border border-green-500/40 p-5 text-center" data-testid="resale-claim-timer-box">
            <div className="text-[10px] tracking-widest text-green-300 uppercase inline-flex items-center gap-1 justify-center">
              <Clock size={11}/> Your waitlist offer expires in
            </div>
            <div className="mt-2 font-mono font-bold text-white text-4xl tracking-widest" data-testid="resale-claim-countdown">
              {timer.hh}:{timer.mm}:{timer.ss}
            </div>
            <div className="text-xs text-gray-400 mt-1">Claim now at the original price of <b className="text-white">${original.toFixed(2)}</b></div>
            <button onClick={buy} disabled={busy || isMine}
              data-testid="resale-claim-buy-original"
              className="mt-4 w-full px-3 py-2.5 rounded-lg text-sm inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white disabled:opacity-60">
              {busy ? <><Loader2 size={14} className="animate-spin"/> Redirecting…</> : <><Ticket size={14}/> Claim my ticket · ${original.toFixed(2)}</>}
            </button>
            <div className="text-[11px] text-gray-500 mt-2">If you don't claim in time, the ticket goes public at ${publicPrice.toFixed(2)} (25% off).</div>
          </div>
        )}

        {isReserved && !listing.isWaitlistClaimant && !isMine && (
          <div className="rounded-lg bg-yellow-500/10 border border-yellow-500/40 p-4 text-xs text-yellow-200" data-testid="resale-claim-reserved-other">
            This listing is currently reserved for a waitlisted attendee. If they don't claim it, it will drop to 25% off soon.
          </div>
        )}

        {isPublic && !isMine && (
          <div className="rounded-xl bg-yellow-500/10 border border-yellow-500/40 p-4 text-center" data-testid="resale-claim-public-box">
            <div className="inline-flex items-center gap-1 justify-center text-yellow-200 text-xs">
              <CheckCircle2 size={12}/> Now available to the public at 25% off
            </div>
            <div className="mt-2 flex items-baseline gap-2 justify-center">
              <div className="text-3xl font-bold text-green-400">${publicPrice.toFixed(2)}</div>
              <div className="text-sm text-gray-500 line-through">${original.toFixed(2)}</div>
            </div>
            <button onClick={buy} disabled={busy}
              data-testid="resale-claim-buy-public"
              className="mt-4 w-full px-3 py-2.5 rounded-lg text-sm inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white disabled:opacity-60">
              {busy ? <><Loader2 size={14} className="animate-spin"/> Redirecting…</> : <><Ticket size={14}/> Buy at ${publicPrice.toFixed(2)}</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
